import { useState } from "react";
import { get } from "lodash";
import { FilterType } from "routes/group/components/AttendantDataTable";
import { useNavigate, useParams } from "react-router-dom";
import { useGetDetail } from "routes/group/hooks/useGetDetail";
import { useGetEvents } from "routes/group/hooks/useGetEvents";
import useQueryParams from "routes/group/hooks/useQueryParams";
import { useGetAttendants } from "routes/group/hooks/useGetAttendants";
import { useGetGroupHistory } from "routes/group/hooks/useGetGroupHistory";
import { isHaveActionPermission } from "utills/helpers";
import { ClockIcon, PencilIcon } from "@heroicons/react/outline";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import DetailForm from "routes/group/components/DetailForm";
import Button from "components/button";
import LogHistoryModal from "routes/group/components/LogHistoryModal";

const GroupManagementDetailPage = () => {
  const PAGE_SIZE = 10;

  const { id } = useParams();
  const { searchParams, paramsObject, setQueryParams } = useQueryParams();

  // Attendant query params
  const attendantPage = searchParams.get("attendant_page")
    ? Number(searchParams.get("attendant_page"))
    : 1;
  const attendantQuery = searchParams.get("attendant_q") ?? "";
  const attendantGroupId = searchParams.get("attendant_group_id") ?? "";
  const attendantPaymentStatus =
    searchParams.get("attendant_payment_status") ?? "";

  const navigate = useNavigate();

  const { data, loading } = useGetDetail(id ?? "");

  const {
    data: attendants,
    loading: attendantsLoading,
    paging: attendantPaging,
    params: attendantParams,
    setParams: setAttendantParams,
    fetchData: fetchAttendantData,
  } = useGetAttendants(id ?? "", {
    page: attendantPage,
    q: attendantQuery,
    group_id: attendantGroupId,
    payment_status: attendantPaymentStatus,
    limit: PAGE_SIZE,
  });

  const { data: events, loading: eventsLoading } = useGetEvents();

  const {
    data: logData,
    loading: logLoading,
    paging: logPaging,
    params: logParams,
    setParams: setLogParams,
    fetchData: fetchLogData,
  } = useGetGroupHistory(id ?? "", {
    page: 1,
    limit: 5,
  });

  const [attendantFilter, setAttendantFilter] = useState<FilterType>({
    page: attendantPage,
    q: attendantQuery,
    group_id: attendantGroupId,
    payment_status: attendantPaymentStatus,
  });

  const [openLogHistoryModal, setOpenLogHistoryModal] =
    useState<boolean>(false);

  const [logFilter, setLogFilter] = useState<{ page: number }>({ page: 1 });

  const handleChangeAttendantFilter = (key: string, value: string) => {
    setAttendantFilter({
      ...attendantFilter,
      [key]: value,
    });
  };

  const handleChangeAttendantPage = (page: number) => {
    setAttendantFilter({ ...attendantFilter, page: page });

    // Update URL search params
    setQueryParams({
      attendant_page: page.toString(),
    });
    setAttendantParams({ ...attendantParams, page: page }); // Update API params
  };

  const handleSearchAttendant = async (data: FilterType) => {
    console.log("Search:", data);

    // Update URL params
    setQueryParams({
      attendant_page: "1",
      attendant_q: attendantFilter.q,
      attendant_group_id: attendantFilter.group_id,
      attendant_payment_status: attendantFilter.payment_status,
    });

    // Update API params
    setAttendantParams({
      page: 1,
      q: attendantFilter.q,
      group_id: attendantFilter.group_id,
      payment_status: attendantFilter.payment_status,
      limit: PAGE_SIZE,
    });
  };

  // Handle log history
  const handleCloseLogHistoryModal = () => {
    setOpenLogHistoryModal(false);
  };

  const handleLogHistoryPageChange = (value: number) => {
    setLogFilter({ ...logFilter, page: value });

    // Update API search params
    setLogParams({
      ...logParams,
      page: value,
    });
  };

  return (
    <section className="wrapper">
      {(loading || attendantsLoading || eventsLoading) && <FullScreenLoading />}

      {/* Log History Modal */}
      {data && (
        <LogHistoryModal
          open={openLogHistoryModal}
          targetGroup={data}
          handleClose={handleCloseLogHistoryModal}
          handlePageChange={handleLogHistoryPageChange}
          data={logData}
          loading={logLoading}
          paging={logPaging}
        />
      )}

      <Breadcrumbs
        customPageLabel="Detail Group"
        additionalLabel={
          <div className="flex gap-x-3">
            <Button
              type="button"
              className="w-11 h-11 bg-white rounded-md px-3 text-brand-500 py-2
                            border-solid border-brand-500 border-[1px]
                            hover:bg-white active:bg-white active:opacity-85 sm:w-auto"
              onClick={() => {
                setOpenLogHistoryModal(true);
              }}
            >
              <ClockIcon className="size-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">Log</span>
            </Button>

            {isHaveActionPermission("", "group_buy_management", "edit") && (
              <Button
                className="w-11 h-11 sm:w-auto"
                onClick={() => {
                  navigate(
                    `/group-management/edit/${id ?? ""}?event_id=${get(
                      data,
                      "event_id",
                      ""
                    )}`
                  );
                }}
              >
                <PencilIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Edit</span>
              </Button>
            )}
          </div>
        }
      />

      {!loading && !attendantsLoading && !eventsLoading && !data && (
        <div className="w-full min-h-[calc(100%-72px)] bg-[#f6f5fe] flex flex-col items-center py-[50px]">
          <div className="text-brand-500 text-center">
            <div className="text-36 font-700 mb-[16px]">Group not found</div>
            <div className="text-20 mb-[8px]">Group does not exist</div>
            <div className="text-20">
              Please contact your Administrator for assistance
            </div>
          </div>
        </div>
      )}

      {!loading && !attendantsLoading && !eventsLoading && data && (
        <DetailForm
          data={data}
          events={events}
          attendants={attendants}
          attendantPaging={attendantPaging}
          handleChangeAttendantPage={handleChangeAttendantPage}
          attendantFilter={attendantFilter}
          handleChangeAttendantFilter={handleChangeAttendantFilter}
          handleSearchAttendant={handleSearchAttendant}
        />
      )}
    </section>
  );
};

export default GroupManagementDetailPage;
